import { forwardRef, transition } from '@hover/blueprint';
import { iBookmark, iHeart } from '@hover/icons';
import React from 'react';

import { FeatureFlag } from 'src/shared/constants';
import { useTreatment } from 'src/shared/hooks';

import {
  useFavoriteHandler,
  useHomeownerAppContext,
  useHomeownerTranslate,
  useSaveToProject,
} from '../../hooks';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import type { AiOrSampleDisplayImage } from '../../types';
import type { TileButtonProps, TileIconButtonProps } from '../TileButton';
import { TileIconButton } from '../TileButton';

export interface SaveTileIconButtonProps
  extends Omit<TileButtonProps, 'label' | 'icon'> {
  image: AiOrSampleDisplayImage;
}

const ToggleTileIconButton = forwardRef<
  TileIconButtonProps & { isToggled: boolean },
  'button'
>(({ isToggled, sx, ...props }, ref) => (
  <TileIconButton
    sx={{
      ...sx,
      '& svg': {
        fill: isToggled ? 'current' : 'transparent',
        ...transition.easing250('fill'),
      },
    }}
    {...props}
    ref={ref}
  />
));

const FavoriteSaveTileIconButton = ({
  image,
  ...props
}: SaveTileIconButtonProps) => {
  const t = useHomeownerTranslate();

  const { setCurrentMenu } = useHomeownerAppContext();

  const handleFavoriteClick = useFavoriteHandler({
    image,
    onFavoriteSample: () => setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO),
  });

  const isFavorited = !!image.favoritedAt;
  const favoriteAriaLabel = isFavorited
    ? t('fullscreenImage.unmarkFavorite')
    : t('fullscreenImage.markFavorite');

  return (
    <ToggleTileIconButton
      aria-label={favoriteAriaLabel}
      icon={iHeart}
      isToggled={isFavorited}
      label={t('fullscreenImage.favorite')}
      onClick={handleFavoriteClick}
      {...props}
    />
  );
};

const ProjectSaveTileIconButton = ({
  image,
  ...props
}: SaveTileIconButtonProps) => {
  const t = useHomeownerTranslate();

  const { isSaved, handleSave } = useSaveToProject({
    image,
    // TODO: update this per the projects tracking plan
    location: 'fullscreen',
  });

  return (
    <ToggleTileIconButton
      aria-label={t('projects.save')}
      icon={iBookmark}
      isToggled={isSaved}
      label={t('projects.save')}
      onClick={handleSave}
      {...props}
    />
  );
};

export const SaveTileIconButton = (props: SaveTileIconButtonProps) => {
  const isProjectsEnabled =
    useTreatment(FeatureFlag.Web.HOMEOWNER_APP_PROJECTS) === 'on';

  return isProjectsEnabled ? (
    <ProjectSaveTileIconButton {...props} />
  ) : (
    <FavoriteSaveTileIconButton {...props} />
  );
};
